/**
 * Floating chat panel.
 *
 * Opens in place from the launcher instead of navigating to /communication:
 * chat is something you glance at mid-task, and being thrown onto a full page
 * loses whatever you were doing. The full page still exists for long sessions —
 * "Hape të plotë" in the header goes there.
 *
 * Reuses services/chatApi, the same endpoints the page uses. No parallel
 * transport, no second source of truth for unread counts.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  chatOverview, chatMessages, chatSend, chatMarkRead, chatReact,
} from '../../services/chatApi';
import './ChatPanel.css';

/** While a thread is open, how often to pull new messages. */
const THREAD_POLL_MS = 8_000;
/** While only the list is showing, a slower beat is enough. */
const LIST_POLL_MS = 30_000;

const initial = (name) => String(name || '?').trim().charAt(0).toUpperCase() || '?';

const clock = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit', hour12: false });
};

/** Preview line under a conversation name — mirrors the full page's wording. */
function preview(c) {
  if (!c.last) return c.kind === 'dm' ? 'Ende asnjë mesazh' : (c.purpose || 'Ende asnjë mesazh');
  const who = c.last.mine ? 'Ti' : (c.last.author || '');
  const body = String(c.last.body || '').replace(/\s+/g, ' ').trim();
  return who ? `${who}: ${body}` : body;
}

export default function ChatPanel({ open, onClose, onUnreadChange }) {
  const history = useHistory();

  const [data, setData]       = useState({ channels: [], dms: [], unread: 0 });
  const [tab, setTab]         = useState('dms');
  const [active, setActive]   = useState(null);   // the open conversation
  const [messages, setMessages] = useState([]);
  const [draft, setDraft]     = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState('');

  const endRef = useRef(null);

  /* ── conversation list ── */
  const loadList = useCallback(async () => {
    try {
      const d = await chatOverview();
      setData(d || { channels: [], dms: [], unread: 0 });
      if (onUnreadChange) onUnreadChange(d?.unread || 0);
    } catch {
      setError('Bisedat nuk u ngarkuan.');
    }
  }, [onUnreadChange]);

  useEffect(() => {
    if (!open) return undefined;
    loadList();
    const t = setInterval(loadList, LIST_POLL_MS);
    return () => clearInterval(t);
  }, [open, loadList]);

  /* ── open thread ── */
  const loadThread = useCallback(async (channelId) => {
    try {
      const r = await chatMessages(channelId);
      setMessages(r?.messages || []);
    } catch {
      setError('Mesazhet nuk u ngarkuan.');
    }
  }, []);

  const openConversation = useCallback(async (c) => {
    setActive(c);
    setMessages([]);
    setError('');
    setLoading(true);
    await loadThread(c.id);
    setLoading(false);
    // Clearing the badge is best-effort; a failure here must not block reading.
    chatMarkRead(c.id).then(loadList).catch(() => {});
  }, [loadThread, loadList]);

  useEffect(() => {
    if (!open || !active) return undefined;
    const t = setInterval(() => loadThread(active.id), THREAD_POLL_MS);
    return () => clearInterval(t);
  }, [open, active, loadThread]);

  // Keep the newest message in view as the thread grows.
  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: 'end' });
  }, [messages, active]);

  /* ── send ── */
  const send = async () => {
    const body = draft.trim();
    if (!body || !active || sending) return;
    setSending(true);
    setError('');
    try {
      await chatSend(active.id, body);
      setDraft('');
      await loadThread(active.id);   // the server's copy, not a local echo
      loadList();
    } catch {
      setError('Mesazhi nuk u dërgua.');
    } finally {
      setSending(false);
    }
  };

  /** Reago / hiq reagimin. Rifreskon fillin, që numërimi të vijë nga serveri. */
  const toggleReaction = async (m, emoji) => {
    if (!active) return;
    try {
      await chatReact(active.id, m.id, emoji);
      await loadThread(active.id);
    } catch (e) {
      setError(e.message);
    }
  };

  const rows = useMemo(() => {
    const channels = (data.channels || []).filter((c) => c.icon !== 'megaphone');
    return tab === 'dms' ? (data.dms || []) : channels;
  }, [data, tab]);

  if (!open) return null;

  return (
    <section className="nx-chat" role="dialog" aria-label="Bisedat">
      {/* Header */}
      <div className="nx-chat-head">
        {active && (
          <button className="nx-chat-headbtn" onClick={() => { setActive(null); setMessages([]); }}
            aria-label="Kthehu te bisedat" title="Kthehu">←</button>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>{active ? active.name : 'Bisedat'}</h3>
          <p>{active ? (active.kind === 'dm' ? 'Mesazh direkt' : 'Kanal') : 'Ekipi yt, gjithmonë i lidhur'}</p>
        </div>
        <button className="nx-chat-headbtn"
          onClick={() => { onClose(); history.push('/communication'); }}
          aria-label="Hape të plotë" title="Hape të plotë">⤢</button>
        <button className="nx-chat-headbtn" onClick={onClose} aria-label="Mbyll" title="Mbyll">×</button>
      </div>

      {/* List */}
      {!active && (
        <>
          <div className="nx-chat-tabs">
            <button className={`nx-chat-tab${tab === 'dms' ? ' is-on' : ''}`} onClick={() => setTab('dms')}>
              Mesazhet
            </button>
            <button className={`nx-chat-tab${tab === 'channels' ? ' is-on' : ''}`} onClick={() => setTab('channels')}>
              Kanalet
            </button>
          </div>
          <div className="nx-chat-list">
            {rows.length === 0 ? (
              <div className="nx-chat-empty">
                {tab === 'dms' ? 'Ende asnjë bisedë.' : 'Ende asnjë kanal.'}
              </div>
            ) : rows.map((c) => (
              <button key={c.id} className="nx-chat-row" onClick={() => openConversation(c)}>
                <span className="nx-chat-av">{initial(c.name)}</span>
                <span className="nx-chat-rowmain">
                  <span className="nx-chat-rowname">{c.name}</span>
                  <span className="nx-chat-rowlast">{preview(c)}</span>
                </span>
                {c.unread > 0 && <span className="nx-chat-badge">{c.unread > 99 ? '99+' : c.unread}</span>}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Thread */}
      {active && (
        <>
          <div className="nx-chat-msgs">
            {loading ? (
              <div className="nx-chat-empty">Duke ngarkuar…</div>
            ) : messages.length === 0 ? (
              <div className="nx-chat-empty">Ende asnjë mesazh. Shkruaj i pari.</div>
            ) : messages.map((m) => (
              <div key={m.id}>
                <div className={`nx-chat-meta${m.mine ? ' me' : ''}`}>
                  {m.mine ? 'Ti' : (m.author?.name || m.author || '')} · {clock(m.at)}
                </div>
                <div className={`nx-chat-bubble ${m.mine ? 'me' : 'them'}`}>
                  {/* Një mesazh i dërguar si përgjigje duhet ta mbajë kontekstin edhe
                      këtu — pa citimin, te paneli duket si mendim pa lidhje. */}
                  {m.reply && (
                    <div className="nx-chat-quote">
                      <b>{m.reply.name || 'Përdorues'}</b>
                      <span>{m.reply.body}</span>
                    </div>
                  )}
                  {m.body}
                </div>
                {/* Reagimet shfaqen e ndërrohen, por përgjigjja dhe fshirja rrinë te
                    faqja e plotë (⤢): paneli është 340px dhe do të mbytej. */}
                {!!m.reactions?.length && (
                  <div className={`nx-chat-reacts${m.mine ? ' me' : ''}`}>
                    {m.reactions.map((r) => (
                      <button
                        key={r.emoji}
                        type="button"
                        className={r.mine ? 'on' : ''}
                        onClick={() => toggleReaction(m, r.emoji)}
                        aria-label={`${r.emoji} ${r.count}`}
                      >
                        {r.emoji} {r.count}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={endRef} />
          </div>

          <div className="nx-chat-foot">
            <textarea
              className="nx-chat-input"
              placeholder="Shkruaj një mesazh…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter breaks the line — the same contract
                // the full page states in its placeholder.
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              rows={1}
            />
            <button className="nx-chat-send" onClick={send} disabled={sending || !draft.trim()} aria-label="Dërgo">
              <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M2 21l21-9L2 3v7l15 2-15 2z" />
              </svg>
            </button>
          </div>
        </>
      )}

      {error && <div className="nx-chat-empty" style={{ color: '#b91c1c', paddingTop: 0 }}>{error}</div>}
    </section>
  );
}
